import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../libs/firebaseConfig";
import { buildDeviceContext, getAccessControlSettings } from "./accessControl";

function isAdminRole(userDoc) {
  const role = userDoc?.role;
  if (Array.isArray(role)) {
    return role.map((x) => String(x).toLowerCase()).includes("admin");
  }
  return String(role || "").toLowerCase() === "admin";
}

function toDateSafe(value) {
  try {
    if (!value) return null;
    if (value?.toDate) return value.toDate();
    if (value instanceof Date) return value;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function isExpired(updatedAt, timeoutSeconds = 120) {
  const d = toDateSafe(updatedAt);
  if (!d) return true;
  return Date.now() - d.getTime() > Number(timeoutSeconds || 120) * 1000;
}

function sessionRef(uid) {
  return doc(db, "users", uid, "activeCourseSession", "current");
}

export async function claimCourseActiveDevice({
  user,
  userDoc,
  courseId,
  lessonId = "",
  forceClaim = true,
  deviceOverride = null,
}) {
  const uid = user?.uid;
  if (!uid || !courseId) {
    return { ok: false, enabled: false, skipped: true, reason: "missing_user_or_course" };
  }

  const settings = await getAccessControlSettings();

  if (!settings?.enableCourseActiveDeviceLock) {
    return { ok: true, enabled: false, skipped: true, reason: "disabled" };
  }

  if (
    settings?.enableCourseActiveDeviceLockAdminOnly === true &&
    !isAdminRole(userDoc)
  ) {
    return { ok: true, enabled: false, skipped: true, reason: "admin_only" };
  }

  const device = deviceOverride || (await buildDeviceContext());
  const ref = sessionRef(uid);

  if (!forceClaim) {
    const snap = await getDoc(ref);
    const current = snap.exists() ? snap.data() : null;

    const currentSig = String(current?.deviceSignature || "");
    const sameDevice = currentSig && currentSig === device.signature;
    const expired =
      !current ||
      isExpired(current.updatedAt, settings.courseActiveTimeoutSeconds || 120);

    if (current && !sameDevice && !expired) {
      return {
        ok: false,
        enabled: true,
        reason: "active_on_other_device",
        current,
        device,
        settings,
      };
    }
  }

  await setDoc(
    ref,
    {
      uid,
      courseId,
      lessonId,
      deviceSignature: device.signature,
      slot: device.slot,
      label: device.label,
      userAgent: device.meta.userAgent,
      platform: device.meta.platform,
      timezone: device.meta.timezone,
      screenSize: device.meta.screenSize,
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    },
    { merge: true }
  );

  return {
    ok: true,
    enabled: true,
    device,
    settings,
  };
}

export async function heartbeatCourseActiveDevice({
  user,
  userDoc,
  courseId,
  lessonId = "",
  deviceOverride = null,
}) {
  return claimCourseActiveDevice({
    user,
    userDoc,
    courseId,
    lessonId,
    forceClaim: false,
    deviceOverride,
  });
}

export function subscribeCourseActiveDevice({ uid, onChange, onError }) {
  if (!uid) return () => {};

  return onSnapshot(
    sessionRef(uid),
    (snap) => {
      onChange?.(snap.exists() ? snap.data() : null);
    },
    (error) => {
      console.error("activeCourseSession snapshot error:", error);
      onError?.(error);
    }
  );
}
